import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import Navigation from "../components/Navigation";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Search,
  UserPlus,
  Users,
  UserCheck,
  UserX,
  Clock,
  MessageCircle,
  Check,
  X,
  Loader2,
  MoreVertical,
  Ban,
  UserMinus,
  Sparkles,
  Heart,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Friends() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("friends");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [friends, setFriends] = useState([]);
  const [onlineCount, setOnlineCount] = useState(0);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  
  // Block dialog state
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [userToBlock, setUserToBlock] = useState(null);

  // Heartbeat for online status
  useEffect(() => {
    const heartbeat = async () => {
      try {
        await axios.post(`${API}/users/heartbeat`);
      } catch (error) {
        // Silent fail
      }
    };

    heartbeat();
    const interval = setInterval(heartbeat, 60000); // Every minute

    return () => {
      clearInterval(interval);
      // Set offline when leaving
      axios.post(`${API}/users/offline`).catch(() => {});
    };
  }, []);

  const fetchFriends = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/friends`);
      setFriends(response.data.friends);
      setOnlineCount(response.data.online_count);
    } catch (error) {
      console.error("Error fetching friends:", error);
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      const [received, sent] = await Promise.all([
        axios.get(`${API}/friends/requests/received`),
        axios.get(`${API}/friends/requests/sent`),
      ]);
      setReceivedRequests(received.data.requests);
      setSentRequests(sent.data.requests);
    } catch (error) {
      console.error("Error fetching requests:", error);
    }
  }, []);

  const fetchSuggestions = useCallback(async () => {
    setLoadingSuggestions(true);
    try {
      const response = await axios.get(`${API}/friends/suggestions`);
      setSuggestions(response.data.suggestions);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  const fetchBlockedUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users/blocked`);
      setBlockedUsers(response.data.blocked_users);
    } catch (error) {
      console.error("Error fetching blocked users:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchFriends(), fetchRequests(), fetchSuggestions(), fetchBlockedUsers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchFriends, fetchRequests, fetchSuggestions, fetchBlockedUsers]);

  const handleSearch = async () => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data.users);
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        handleSearch();
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const sendFriendRequest = async (userId) => {
    try {
      await axios.post(`${API}/friends/request`, { to_user_id: userId });
      toast.success("Friend request sent!");
      handleSearch();
      fetchRequests();
      fetchSuggestions();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send request";
      toast.error(message);
    }
  };

  const acceptRequest = async (requestId) => {
    try {
      await axios.post(`${API}/friends/requests/${requestId}/accept`);
      toast.success("Friend request accepted! +10 points");
      fetchFriends();
      fetchRequests();
      fetchSuggestions();
    } catch (error) {
      toast.error("Failed to accept request");
    }
  };

  const declineRequest = async (requestId) => {
    try {
      await axios.post(`${API}/friends/requests/${requestId}/decline`);
      toast.success("Friend request declined");
      fetchRequests();
    } catch (error) {
      toast.error("Failed to decline request");
    }
  };

  const cancelRequest = async (requestId) => {
    try {
      await axios.delete(`${API}/friends/requests/${requestId}`);
      toast.success("Request cancelled");
      fetchRequests();
    } catch (error) {
      toast.error("Failed to cancel request");
    }
  };

  const unfriend = async (friendId) => {
    if (!confirm("Are you sure you want to remove this friend?")) return;
    try {
      await axios.delete(`${API}/friends/${friendId}`);
      toast.success("Friend removed");
      fetchFriends();
      fetchSuggestions();
    } catch (error) {
      toast.error("Failed to remove friend");
    }
  };

  const openBlockDialog = (userId, userName) => {
    setUserToBlock({ id: userId, name: userName });
    setBlockDialogOpen(true);
  };

  const blockUser = async () => {
    if (!userToBlock) return;
    try {
      await axios.post(`${API}/users/block`, { user_id: userToBlock.id });
      toast.success(`${userToBlock.name} has been blocked`);
      setBlockDialogOpen(false);
      setUserToBlock(null);
      fetchFriends();
      fetchRequests();
      fetchSuggestions();
      fetchBlockedUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to block user";
      toast.error(message);
    }
  };

  const unblockUser = async (userId, userName) => {
    try {
      await axios.delete(`${API}/users/block/${userId}`);
      toast.success(`${userName} has been unblocked`);
      fetchBlockedUsers();
    } catch (error) {
      toast.error("Failed to unblock user");
    }
  };

  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return "Unknown";
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 5) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith("http")) return photo;
    return `${process.env.REACT_APP_BACKEND_URL}${photo}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50">
      <Navigation />

      <main className="max-w-4xl mx-auto px-4 py-6 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2" data-testid="friends-page-title">
              <Users className="w-8 h-8 text-amber-600" />
              Friends
            </h1>
            <p className="text-gray-600">
              {friends.length} friends • {onlineCount} online now
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <Card className="mb-6 bg-white/80 backdrop-blur">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for friends by name..."
                className="pl-10 h-12 text-lg rounded-full bg-gray-50"
                data-testid="friend-search-input"
              />
              {searching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-500 animate-spin" />
              )}
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-4 space-y-3" data-testid="search-results">
                <h3 className="text-sm font-medium text-gray-500">Search Results</h3>
                {searchResults.map((result) => (
                  <div
                    key={result.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                    data-testid={`search-result-${result.id}`}
                  >
                    <div
                      className="flex items-center gap-3 cursor-pointer"
                      onClick={() => navigate(`/profile/${result.id}`)}
                    >
                      <div className="relative">
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={getPhotoUrl(result.profile_photo)} />
                          <AvatarFallback className="bg-amber-500 text-white">
                            {result.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        {result.is_online && (
                          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {result.name}
                          <Badge variant="outline" className="text-xs">
                            {result.tier}
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-500">
                          {result.mutual_friends > 0 && `${result.mutual_friends} mutual friends • `}
                          {result.is_online ? (
                            <span className="text-green-600">Online</span>
                          ) : (
                            <span>Last seen {formatLastSeen(result.last_seen)}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {result.friendship_status === "none" && (
                        <Button
                          onClick={() => sendFriendRequest(result.id)}
                          size="sm"
                          className="bg-amber-500 hover:bg-amber-600"
                          data-testid={`add-friend-${result.id}`}
                        >
                          <UserPlus className="w-4 h-4 mr-1" />
                          Add Friend
                        </Button>
                      )}
                      {result.friendship_status === "pending_sent" && (
                        <Badge variant="secondary">
                          <Clock className="w-3 h-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                      {result.friendship_status === "pending_received" && (
                        <Badge className="bg-green-500">
                          <UserCheck className="w-3 h-3 mr-1" />
                          Respond
                        </Badge>
                      )}
                      {result.friendship_status === "friends" && (
                        <Badge className="bg-blue-500">
                          <Users className="w-3 h-3 mr-1" />
                          Friends
                        </Badge>
                      )}
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/profile/${result.id}`)}>
                            <MessageCircle className="w-4 h-4 mr-2" />
                            View Profile
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => openBlockDialog(result.id, result.name)}
                          >
                            <Ban className="w-4 h-4 mr-2" />
                            Block User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* People You May Know */}
        {suggestions.length > 0 && (
          <Card className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="p-4">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                People You May Know
              </h3>
              <div className="flex gap-4 overflow-x-auto pb-2" data-testid="friend-suggestions">
                {suggestions.slice(0, 6).map((suggestion) => (
                  <div
                    key={suggestion.id}
                    className="flex-shrink-0 w-36 bg-white rounded-xl p-4 shadow-sm text-center"
                    data-testid={`suggestion-${suggestion.id}`}
                  >
                    <div className="relative inline-block mb-2">
                      <Avatar 
                        className="w-16 h-16 cursor-pointer" 
                        onClick={() => navigate(`/profile/${suggestion.id}`)}
                      >
                        <AvatarImage src={getPhotoUrl(suggestion.profile_photo)} />
                        <AvatarFallback className="bg-amber-500 text-white text-xl">
                          {suggestion.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      {suggestion.is_online && (
                        <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
                      )}
                    </div>
                    <h4 
                      className="font-medium text-sm truncate cursor-pointer hover:text-amber-600"
                      onClick={() => navigate(`/profile/${suggestion.id}`)}
                    >
                      {suggestion.name}
                    </h4>
                    {suggestion.mutual_friends > 0 && (
                      <p className="text-xs text-gray-500 mb-2">
                        {suggestion.mutual_friends} mutual
                      </p>
                    )}
                    <Button
                      onClick={() => sendFriendRequest(suggestion.id)}
                      size="sm"
                      variant="outline"
                      className="w-full text-xs border-amber-300 hover:bg-amber-50"
                      data-testid={`add-suggestion-${suggestion.id}`}
                    >
                      <UserPlus className="w-3 h-3 mr-1" />
                      Add
                    </Button>
                  </div>
                ))}
                {loadingSuggestions && (
                  <div className="flex-shrink-0 w-36 h-40 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 mb-6">
            <TabsTrigger value="friends" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-friends">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Friends</span> ({friends.length})
            </TabsTrigger>
            <TabsTrigger value="requests" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-requests">
              <UserPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Requests</span> ({receivedRequests.length})
            </TabsTrigger>
            <TabsTrigger value="sent" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-sent">
              <Clock className="w-4 h-4" />
              <span className="hidden sm:inline">Sent</span> ({sentRequests.length})
            </TabsTrigger>
            <TabsTrigger value="blocked" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-blocked">
              <Ban className="w-4 h-4" />
              <span className="hidden sm:inline">Blocked</span> ({blockedUsers.length})
            </TabsTrigger>
          </TabsList>

          {/* Friends List */}
          <TabsContent value="friends" data-testid="friends-list">
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-500" />
              </div>
            ) : friends.length === 0 ? (
              <Card className="bg-white/80">
                <CardContent className="py-12 text-center">
                  <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">No friends yet</h3>
                  <p className="text-gray-500 mt-1">
                    Search for people to add as friends!
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {friends.map((friend) => (
                  <Card key={friend.id} className="bg-white/90 backdrop-blur" data-testid={`friend-${friend.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div
                          className="flex items-center gap-3 cursor-pointer"
                          onClick={() => navigate(`/profile/${friend.id}`)}
                        >
                          <div className="relative">
                            <Avatar className="w-14 h-14">
                              <AvatarImage src={getPhotoUrl(friend.profile_photo)} />
                              <AvatarFallback className="bg-amber-500 text-white text-lg">
                                {friend.name?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            {friend.is_online && (
                              <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
                            )}
                          </div>
                          <div>
                            <div className="font-semibold text-lg flex items-center gap-2">
                              {friend.name}
                              {friend.is_online && (
                                <span className="text-xs text-green-600 font-normal">● Online</span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {friend.tier} •{" "}
                              {friend.is_online
                                ? "Active now"
                                : `Last seen ${formatLastSeen(friend.last_seen)}`}
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-amber-500 hover:bg-amber-600"
                            onClick={() => navigate(`/messages/${friend.id}`)}
                            data-testid={`message-${friend.id}`}
                          >
                            <MessageCircle className="w-4 h-4 mr-1" />
                            Message
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/profile/${friend.id}`)}
                            data-testid={`view-profile-${friend.id}`}
                          >
                            Profile
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                className="text-orange-600"
                                onClick={() => unfriend(friend.id)}
                              >
                                <UserMinus className="w-4 h-4 mr-2" />
                                Unfriend
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={() => openBlockDialog(friend.id, friend.name)}
                              >
                                <Ban className="w-4 h-4 mr-2" />
                                Block
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Received Requests */}
          <TabsContent value="requests" data-testid="requests-list">
            {receivedRequests.length === 0 ? (
              <Card className="bg-white/80">
                <CardContent className="py-12 text-center">
                  <UserPlus className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">No pending requests</h3>
                  <p className="text-gray-500 mt-1">
                    When someone sends you a friend request, it will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {receivedRequests.map((request) => (
                  <Card key={request.id} className="bg-white/90 backdrop-blur border-l-4 border-l-amber-500" data-testid={`request-${request.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div
                          className="flex items-center gap-3 cursor-pointer"
                          onClick={() => navigate(`/profile/${request.from_user_id}`)}
                        >
                          <Avatar className="w-14 h-14">
                            <AvatarImage src={getPhotoUrl(request.from_user_photo)} />
                            <AvatarFallback className="bg-amber-500 text-white text-lg">
                              {request.from_user_name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-semibold text-lg">{request.from_user_name}</div>
                            {request.message && (
                              <p className="text-sm text-gray-600 italic">"{request.message}"</p>
                            )}
                            <div className="text-xs text-gray-400">
                              {new Date(request.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            onClick={() => acceptRequest(request.id)}
                            size="sm"
                            className="bg-green-500 hover:bg-green-600"
                            data-testid={`accept-${request.id}`}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Accept
                          </Button>
                          <Button
                            onClick={() => declineRequest(request.id)}
                            variant="outline"
                            size="sm"
                            data-testid={`decline-${request.id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Sent Requests */}
          <TabsContent value="sent" data-testid="sent-requests-list">
            {sentRequests.length === 0 ? (
              <Card className="bg-white/80">
                <CardContent className="py-12 text-center">
                  <Clock className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">No pending requests</h3>
                  <p className="text-gray-500 mt-1">
                    Friend requests you send will appear here until accepted
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {sentRequests.map((request) => (
                  <Card key={request.id} className="bg-white/90 backdrop-blur" data-testid={`sent-${request.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div
                          className="flex items-center gap-3 cursor-pointer"
                          onClick={() => navigate(`/profile/${request.to_user_id}`)}
                        >
                          <Avatar className="w-14 h-14">
                            <AvatarImage src={getPhotoUrl(request.to_user_photo)} />
                            <AvatarFallback className="bg-gray-400 text-white text-lg">
                              {request.to_user_name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-semibold text-lg">{request.to_user_name}</div>
                            <div className="text-sm text-amber-600 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Pending since {new Date(request.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>

                        <Button
                          onClick={() => cancelRequest(request.id)}
                          variant="outline"
                          size="sm"
                          data-testid={`cancel-${request.id}`}
                        >
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Blocked Users */}
          <TabsContent value="blocked" data-testid="blocked-users-list">
            {blockedUsers.length === 0 ? (
              <Card className="bg-white/80">
                <CardContent className="py-12 text-center">
                  <Ban className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">No blocked users</h3>
                  <p className="text-gray-500 mt-1">
                    Users you block will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {blockedUsers.map((blockedUser) => (
                  <Card key={blockedUser.id} className="bg-white/90 backdrop-blur border-l-4 border-l-red-400" data-testid={`blocked-${blockedUser.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-14 h-14 opacity-60">
                            <AvatarImage src={getPhotoUrl(blockedUser.profile_photo)} />
                            <AvatarFallback className="bg-gray-400 text-white text-lg">
                              {blockedUser.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-semibold text-lg text-gray-600">{blockedUser.name}</div>
                            <div className="text-sm text-red-500 flex items-center gap-1">
                              <Ban className="w-3 h-3" />
                              Blocked
                            </div>
                          </div>
                        </div>

                        <Button
                          onClick={() => unblockUser(blockedUser.id, blockedUser.name)}
                          variant="outline"
                          size="sm"
                          className="border-green-300 text-green-600 hover:bg-green-50"
                          data-testid={`unblock-${blockedUser.id}`}
                        >
                          Unblock
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Block Confirmation Dialog */}
      <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="w-5 h-5" />
              Block {userToBlock?.name}?
            </DialogTitle>
            <DialogDescription>
              Blocking this user will:
              <ul className="list-disc list-inside mt-2 space-y-1 text-left">
                <li>Remove them from your friends list</li>
                <li>Cancel any pending friend requests</li>
                <li>Prevent them from seeing your posts</li>
                <li>Prevent you from seeing their posts</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={blockUser}
              data-testid="confirm-block-btn"
            >
              <Ban className="w-4 h-4 mr-2" />
              Block User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
